'use client';

import { createContext, useContext, useMemo, ReactNode, useEffect, useRef } from 'react';
import { create, StoreApi, UseBoundStore, useStore } from 'zustand';
import { useAuth } from '@clerk/nextjs';
import { useUserStore } from '@/stores/UserStore';
import { UserFirebaseTypeWithId } from '@poppy/firebase';
import { applyEntitlementOverlay, type ProductEntitlement } from '@poppy/utils';

// -------------------------------------
// Types
// -------------------------------------

export interface AuthState {
  isGuest: boolean;
  user: UserFirebaseTypeWithId;
  // v2 product entitlement resolved server-side (protected layout) and applied
  // over the live user. Read-time only — never persisted. Exposed so consumers
  // (e.g. CreditsContext) can factor product credits into displayed totals.
  productEntitlement?: ProductEntitlement;
  getToken: ReturnType<typeof useAuth>['getToken'] | null;
}

export type AuthStoreState = AuthState;

// -------------------------------------
// Store Factory
// -------------------------------------

export const createAuthStore = (
  user: UserFirebaseTypeWithId,
  isGuest: boolean,
  productEntitlement?: ProductEntitlement
) =>
  create<AuthStoreState>(() => ({
    isGuest,
    user,
    productEntitlement,
    getToken: null,
  }));

export let authStore: UseBoundStore<StoreApi<AuthState>> | null = null;

// -------------------------------------
// Context Setup
// -------------------------------------

const AuthStoreContext = createContext<ReturnType<typeof createAuthStore> | null>(null);

export const AuthStoreProvider = ({
  children,
  user: initialUser,
  isGuest = false,
  productEntitlement,
}: {
  children: ReactNode;
  user: UserFirebaseTypeWithId;
  isGuest?: boolean;
  productEntitlement?: ProductEntitlement;
}) => {
  const globalUser = useUserStore(state => state.user); // Get user from global UserStore
  const { getToken } = useAuth();

  // Use global user if available, fallback to initial user for protected routes,
  // then overlay the server-resolved product entitlement (plan + roles). The
  // overlay is re-applied here so it survives live UserStore doc updates, which
  // carry no entitlement. Identity when there's no product → user unchanged.
  const user = useMemo(
    () => applyEntitlementOverlay(globalUser || initialUser, productEntitlement),
    [globalUser, initialUser, productEntitlement]
  );

  authStore = useRef(createAuthStore(user, isGuest, productEntitlement)).current;

  useEffect(() => {
    if (!user) return;
    // Update store with current user and token
    authStore?.setState({
      isGuest,
      user: user,
      productEntitlement,
      getToken: getToken,
    });
  }, [user, getToken, isGuest, productEntitlement]);

  return <AuthStoreContext.Provider value={authStore}>{children}</AuthStoreContext.Provider>;
};

// -------------------------------------
// Hook
// -------------------------------------

export function useAuthStore<T>(selector: (state: AuthStoreState) => T): T {
  const store = useContext(AuthStoreContext);
  if (!store) {
    throw new Error('useAuthStore must be used within an AuthStoreProvider');
  }
  return useStore(store, selector);
}
